import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { AppConstants } from '../commons/app-constants';
import { Theme } from '../commons/theme-constants';

export const SYSTEM_THEME_VALUE = 'system';

const DARK_MODE_CLASS = 'app-dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  private mediaListener: ((e: MediaQueryListEvent) => void) | null = null;

  private readonly _isDark$ = new BehaviorSubject<boolean>(false);
  readonly isDark$ = this._isDark$.asObservable();

  get isDark(): boolean { return this._isDark$.value; }

  get isAutoMode(): boolean {
    const stored = localStorage.getItem(AppConstants.THEME);
    return !stored || stored === SYSTEM_THEME_VALUE;
  }

  constructor(@Inject(DOCUMENT) private document: Document) {
    const stored = localStorage.getItem(AppConstants.THEME);
    if (!stored || stored === SYSTEM_THEME_VALUE) {
      // No saved preference — follow OS
      this._applyTheme(this._resolveSystemTheme());
      this._startSystemListener();
    } else {
      this._applyTheme(stored);
    }
  }

  /** Apply a named theme (or 'system') and persist the choice. */
  switchTheme(theme: string): void {
    if (theme === SYSTEM_THEME_VALUE) {
      this.enableAutoMode();
      return;
    }
    this._stopSystemListener();
    localStorage.setItem(AppConstants.THEME, theme);
    this._applyTheme(theme);
  }

  /** Switch to OS-following mode: persists 'system', starts listening. */
  enableAutoMode(): void {
    localStorage.setItem(AppConstants.THEME, SYSTEM_THEME_VALUE);
    this._applyTheme(this._resolveSystemTheme());
    this._startSystemListener();
  }

  /** What the OS currently prefers. */
  getSystemTheme(): string {
    return this._resolveSystemTheme();
  }

  // ── Internal helpers ─────────────────────────────────────────────────────

  private _resolveSystemTheme(): string {
    return this.mediaQuery.matches ? Theme.Dark : Theme.Light;
  }

  private _applyTheme(theme: string): void {
    const isDark = theme === Theme.Dark;
    this.document.documentElement.classList.toggle(DARK_MODE_CLASS, isDark);
    this._isDark$.next(isDark);
  }

  private _startSystemListener(): void {
    this._stopSystemListener();
    this.mediaListener = () => {
      if (this.isAutoMode) this._applyTheme(this._resolveSystemTheme());
    };
    this.mediaQuery.addEventListener('change', this.mediaListener);
  }

  private _stopSystemListener(): void {
    if (this.mediaListener) {
      this.mediaQuery.removeEventListener('change', this.mediaListener);
      this.mediaListener = null;
    }
  }
}
